import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import {
    Box, Typography, Stack, Button, IconButton, TextField, InputAdornment,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, Pagination, Tooltip, Avatar,
    FormControl, InputLabel, Select, MenuItem, Skeleton,
    useMediaQuery,
} from "@mui/material";
import {
    Search, Close, PhoneForwarded, ErrorOutline, BusinessCenter,
    Refresh,
} from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";

/* ── Design tokens ── */
const C = {
    indigo: "#4338ca", indigo2: "#6366f1", indigoSoft: "#eef2ff", indigoBd: "#c7d2fe",
    green: "#16a34a", greenSoft: "#f0fdf4", greenBd: "#bbf7d0",
    red: "#dc2626",
    page: "#f8fafc", white: "#ffffff", border: "#e2e8f0", border2: "#f1f5f9",
    txt1: "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
    sans: "'Geist', system-ui, sans-serif", mono: "'Geist Mono', monospace",
};

const PAGE_SIZE = 20;

const AV_COLORS = ["#6366f1","#0ea5e9","#8b5cf6","#ec4899","#f59e0b","#22c55e","#ef4444"];
const avColor = n => {
    if (!n) return C.txt4;
    let h = 0;
    for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff;
    return AV_COLORS[Math.abs(h) % AV_COLORS.length];
};
const initials = n => {
    if (!n) return "?";
    const p = n.trim().split(" ");
    return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase();
};

/* ── Styled ── */
const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        background: C.white,
        borderRadius: 8,
        fontSize: 13.5,
        "& fieldset": { borderColor: C.border },
    },
});

const CallBtn = styled(Button)({
    borderRadius: 6,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 11,
    padding: "3px 10px",
    background: C.greenSoft,
    color: C.green,
    border: `1px solid ${C.greenBd}`,
    "&:hover": { background: C.green, color: "#fff" },
});

/* ── 🔥 MOBILE CARD (ONLY NAME + CALL) ── */
function CPCard({ cp, contactPerson, onCall }) {
    const name = contactPerson?.name || cp.billingName || "Unknown";
    const hasNumber = !!contactPerson?.contactNumber;

    return (
        <Box
            sx={{
                background: C.white,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                p: 1.2,
                mb: 1,
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                
                {/* NAME */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 34, height: 34, background: avColor(name), fontSize: 12 }}>
                        {initials(name)}
                    </Avatar>

                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: 13.5,
                            color: C.txt1,
                            // maxWidth: 250,
                            overflow: "hidden",
                            // textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {name}
                    </Typography>
                </Stack>

                {/* CALL */}
                <CallBtn
                    disabled={!hasNumber}
                    onClick={() => hasNumber && onCall(cp, contactPerson)}
                >
                    {hasNumber ? "Call" : "No #"}
                </CallBtn>
            </Stack>
        </Box>
    );
}

/* ── MAIN ── */
export default function CPTab({ onCallNow }) {
    const [cps, setCps] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    const isMobile = useMediaQuery("(max-width:640px)");

    useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const fetchCPs = useCallback(async (pg, sv) => {
        setLoading(true);
        try {
            let q = supabase
                .from("CP")
                .select("cpId, billingName, CpContactPerson(name, contactNumber)", { count: "exact" })
                .eq("approvalStatus", "approved")
                .range((pg - 1) * PAGE_SIZE, pg * PAGE_SIZE - 1);

            if (sv) q = q.ilike("billingName", `%${sv}%`);

            const { data, count } = await q;
            setCps(data || []);
            setTotal(count || 0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCPs(1, search); }, [search, fetchCPs]);

    const getContactPerson = cp => {
        const c = cp.CpContactPerson;
        return Array.isArray(c) ? c[0] : c;
    };

    const handleCall = (cp, contactPerson) => {
        const name = contactPerson?.name || cp.billingName || "Unknown";
        const contactNumber = contactPerson?.contactNumber?.toString() || "";
        onCallNow?.({}, { name, contactNumber });
    };

    return (
        <Box>

            {/* SEARCH */}
            <SearchBox
                size="small"
                placeholder="Search..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setSearchInput("")}>
                                <Close />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />

            {/* LOADING */}
            {loading && (
                <Stack spacing={1}>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} variant="rounded" height={80} />
                    ))}
                </Stack>
            )}

            {/* MOBILE LIST */}
            {!loading && cps.map(cp => (
                <CPCard
                    key={cp.cpId}
                    cp={cp}
                    contactPerson={getContactPerson(cp)}
                    onCall={handleCall}
                />
            ))}

            {/* EMPTY */}
            {!loading && cps.length === 0 && (
                <Box sx={{ py: 7, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13.5, color: C.txt3, fontFamily: C.sans }}>
                        {searchInput ? "No channel partners match your search." : "No approved channel partners found."}
                    </Typography>
                </Box>
            )}

            {/* PAGINATION */}
            {Math.ceil(total / PAGE_SIZE) > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Pagination
                        count={Math.ceil(total / PAGE_SIZE)}
                        page={page}
                        onChange={(_, p) => { setPage(p); fetchCPs(p, search); }}
                        shape="rounded"
                        size="small"
                        sx={{ "& .Mui-selected": { background: `${C.indigo} !important`, color: "#fff" } }}
                    />
                </Box>
            )}

        </Box>
    );
}